import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { outpostStyles, OutpostNavbar, BlurText } from '../components/OutpostShared';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import FooterSection from '../components/FooterSection';

const { FiMail, FiPhone, FiMapPin, FiSend, FiCheck } = FiIcons;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    brief: ''
  });
  
  const [status, setStatus] = useState('idle'); // 'idle' | 'sending' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    // We map formData.email to 'subject' as requested, and brief to 'message'
    const payload = {
      name: formData.name,
      subject: formData.email,
      message: formData.brief,
      timestamp: new Date().toISOString()
    };

    try {
      await fetch('https://connect.pabbly.com/webhook-listener/webhook/IjU3NjEwNTY4MDYzNDA0MzE1MjZmNTUzZCI_3D_pc/IjU3NjcwNTZlMDYzMzA0MzY1MjZjNTUzNzUxMzEi_pc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      setStatus('success');
      setFormData({ name: '', email: '', brief: '' });
      
      // Reset button state after 3 seconds
      setTimeout(() => {
        setStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
      alert('There was an error submitting your brief. Please try again.');
      setTimeout(() => {
        setStatus('idle');
      }, 3000);
    }
  };

  return (
    <div className="outpost-page min-h-screen text-black font-body selection:bg-black/10 flex flex-col">
      <style>{outpostStyles}</style>
      <OutpostNavbar />

      <main className="flex-1 w-full pt-32 pb-24 px-8 md:px-16 lg:px-20 max-w-7xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
        {/* Left Column: Info */}
        <div className="w-full lg:w-1/2 flex flex-col">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="text-sm font-body text-black/60 mb-6 uppercase tracking-widest"
          >
            // Initiate Contact
          </motion.div>
          
          <BlurText 
            text="Let's Build Something Extraordinary." 
            justify="flex-start"
            className="text-5xl md:text-6xl lg:text-7xl font-heading italic text-black leading-[0.9] tracking-[-2px] mb-8 text-left"
          />
          
          <motion.p 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }}
            className="text-lg text-black/80 font-body font-light leading-relaxed max-w-md mb-12"
          >
            We are always looking for visionary partners ready to reclaim their brand and engineer growth. Provide a brief overview of your project, and our team will be in touch.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="flex flex-col gap-6"
          >
            <h4 className="font-heading italic text-2xl text-black mb-2">Direct Lines</h4>
            
            <a href="mailto:hello@wearesanstitre.com" className="flex items-center gap-4 group">
              <div className="liquid-glass w-12 h-12 rounded-full flex items-center justify-center group-hover:bg-black/5 transition-colors">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-black" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-black/50 font-semibold mb-1">Email Us</span>
                <span className="text-black font-medium">hello@wearesanstitre.com</span>
              </div>
            </a>

            <a href="tel:+13105268412" className="flex items-center gap-4 group">
              <div className="liquid-glass w-12 h-12 rounded-full flex items-center justify-center group-hover:bg-black/5 transition-colors">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-black" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-black/50 font-semibold mb-1">Call Us</span>
                <span className="text-black font-medium">(310) 526-8412</span>
              </div>
            </a>

            <div className="flex items-center gap-4 group mt-2">
              <div className="liquid-glass w-12 h-12 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-black" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-widest text-black/50 font-semibold mb-1">Location</span>
                <span className="text-black font-medium">Sacramento, California, USA</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Column: Form */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.8, duration: 0.8 }}
          className="w-full lg:w-1/2 liquid-glass-strong rounded-[2rem] p-8 md:p-12 border border-black/10 shadow-2xl shadow-black/5"
        >
          <h3 className="font-heading italic text-3xl text-black mb-8">Project Brief</h3>
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <label htmlFor="name" className="text-xs uppercase tracking-widest text-black/60 font-semibold">Name</label>
              <input 
                type="text" 
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="bg-white/50 border border-black/10 rounded-xl px-4 py-3 text-black font-body outline-none focus:border-black/30 transition-colors"
                placeholder="John Doe"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-xs uppercase tracking-widest text-black/60 font-semibold">Email Address</label>
              <input 
                type="email" 
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-white/50 border border-black/10 rounded-xl px-4 py-3 text-black font-body outline-none focus:border-black/30 transition-colors"
                placeholder="john@example.com"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="brief" className="text-xs uppercase tracking-widest text-black/60 font-semibold">Short Client Brief</label>
              <textarea 
                id="brief"
                required
                rows={5}
                value={formData.brief}
                onChange={(e) => setFormData({...formData, brief: e.target.value})}
                className="bg-white/50 border border-black/10 rounded-xl px-4 py-3 text-black font-body outline-none focus:border-black/30 transition-colors resize-none"
                placeholder="Tell us what you need..."
              ></textarea>
            </div>

            <button 
              type="submit"
              disabled={status === 'sending'}
              className="mt-4 bg-black text-white rounded-full px-8 py-4 font-body font-medium flex items-center justify-center gap-2 hover:bg-black/80 transition-colors shadow-lg shadow-black/10 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {status === 'sending' ? 'Sending...' : status === 'success' ? 'Message sent!' : 'Submit Brief'}
              {status === 'success' ? (
                <SafeIcon icon={FiCheck} className="w-4 h-4 ml-1" />
              ) : status !== 'sending' ? (
                <SafeIcon icon={FiSend} className="w-4 h-4 ml-1" />
              ) : null}
            </button>
          </form>
        </motion.div>
      </main>

      <div className="mt-auto">
        <FooterSection />
      </div>
    </div>
  );
}